import * as React from 'react';
import { useSelector } from 'react-redux';
import { ModalWindow, UIHeading2 } from '@cian/ui-kit';

import {
  InfoPlate,
  InfrastructureTypeRows,
  OutsideIcon,
  RateInfo,
  TransportAccessibility,
} from '../../../../components/Map/parts/TransportAccessibility';
import { MAX_TRANSPORT_ACCESSIBILITY_RATE } from '../../../../constants/transportAccessibility';
import { selectTransportAccessibilityInfrastructure } from '../../../../selectors/infrastructure';
import { getTransportAccessibility, getTransportAccessibilitySource } from '../../../../selectors/newbuilding';
import { ITransportAccessibility } from '../../../../types/newbuilding/transportAccessibility';
import { TTAInfrastructureType } from '../../../../types/map';
import { prepareRate, prepareTransportAccessibility } from '../../../TransportAccessibilityRate/helpers';
import { getOutsideRows } from './helpers';

export interface ITransportAccessibilityOutsideProps {
  onTransportAccessibilityChange: (infrastructureType: TTAInfrastructureType | null) => void;
}

export const TransportAccessibilityOutside: React.FC<ITransportAccessibilityOutsideProps> = ({
  onTransportAccessibilityChange,
}) => {
  const { rate, rateDetails, title } = useSelector(getTransportAccessibility) as ITransportAccessibility;
  const departmentLink = useSelector(getTransportAccessibilitySource);

  const displayRate = prepareRate(rate);
  const displayMaxRate = String(MAX_TRANSPORT_ACCESSIBILITY_RATE);
  const displayTerms = React.useMemo(() => prepareTransportAccessibility(rateDetails), [rateDetails]);

  const transportAccesibilityInfrastructure = useSelector(selectTransportAccessibilityInfrastructure);
  const clickableRows = React.useMemo(
    () => getOutsideRows(transportAccesibilityInfrastructure),
    [transportAccesibilityInfrastructure],
  );

  const [isModalOpen, setModal] = React.useState(false);
  const openModal = React.useCallback(() => setModal(true), []);
  const closeModal = React.useCallback(() => setModal(false), []);

  const [selectedType, setSelectedType] = React.useState(clickableRows[0]?.id);

  React.useEffect(() => {
    onTransportAccessibilityChange(selectedType as TTAInfrastructureType);

    return () => onTransportAccessibilityChange(null);
  }, [onTransportAccessibilityChange]);

  const handleRowClick = React.useCallback((infrastructureType: TTAInfrastructureType) => {
    onTransportAccessibilityChange(infrastructureType);
    setSelectedType(infrastructureType);
  }, []);

  return (
    <>
      <InfoPlate
        icon={<OutsideIcon />}
        content={<RateInfo onInfoIconClick={openModal} rate={rate} />}
        data-testid="TransportAccessibilityOutsideInfo"
        withHorizontalMargin
      />
      <InfrastructureTypeRows
        clickableRows={clickableRows}
        nonClickableRows={[]}
        onRowClick={handleRowClick}
        selectedItemId={selectedType}
      />
      <ModalWindow
        content={
          <TransportAccessibility
            departmentLink={departmentLink}
            maxRate={displayMaxRate}
            rate={displayRate}
            terms={displayTerms}
          />
        }
        header={<UIHeading2>{title}</UIHeading2>}
        escape
        width={759}
        onClose={closeModal}
        open={isModalOpen}
        size="M"
      />
    </>
  );
};
